import { v4 as uuidv4 } from "uuid";

import { TCreateJobReqDto } from "@/api/services/main/job/dtos/create-job-req.dto";
import { TGetJobDto } from "@/api/services/main/job/dtos/get-job.dto";
import { DATE_FORMAT_FOR_API } from "@/constants/datetime";
import { TJobForm, TJobObject } from "@/features/job/types/JobForm";
import { dayjs } from "@/utils/dayjs";
import { parseNumberWithCommas } from "@/utils/number";

/**
 * Normalizes time strings that exceed 23:59:59 (e.g. Japanese 25-hour format from OCR).
 * "25:00:00" → "01:00:00", "27:30:00" → "03:30:00"
 */
const normalizeTimeString = (time: string | null | undefined): string | null => {
  if (!time) return null;
  const parts = time.split(":");
  if (parts.length < 2) return time;
  const hours = parseInt(parts[0], 10);
  if (isNaN(hours) || hours < 24) return time;
  const normalizedHours = hours % 24;
  return `${String(normalizedHours).padStart(2, "0")}:${parts.slice(1).join(":")}`;
};

const cleanArray = <T>(arr: (T | null | undefined)[] | null | undefined): T[] | null => {
  if (!arr) return null;

  const cleaned = arr.filter((item): item is T => {
    if (item == null) return false;
    if (typeof item === "string") return item.trim() !== "";
    if (typeof item === "object") return Object.keys(item as object).some((k) => (item as Record<string, unknown>)[k] != null);
    return true;
  });

  return cleaned.length > 0 ? cleaned : null;
};
const formatOnboardingSupportField = (data: string[] | TJobObject[] | null | undefined): TJobObject[] | null | undefined => {
  if (!data) return data;

  if (data.length > 0 && data.some((item) => typeof item === "object")) {
    return data as TJobObject[];
  }

  const oldData = data as string[];
  return [...oldData.map((label) => ({ label, checked: true }))];
};

export const cleanObject = <T extends object>(obj: T): Partial<T> => {
  if (!obj || typeof obj !== "object") return {};

  return Object.entries(obj).reduce((acc, [key, value]) => {
    if (value === undefined || value === null) return acc;
    if (typeof value === "string" && value.trim() === "") return acc;
    if (Array.isArray(value) && value.length === 0) return acc;

    (acc as Partial<T>)[key as keyof T] = value;
    return acc;
  }, {} as Partial<T>);
};

export const convertJobFormDataReq = (data: TJobForm): TCreateJobReqDto => {
  const raw = {
    companyId: Number(data.companyId),
    companySpecificIndustryId: data.companySpecificIndustryId ? Number(data.companySpecificIndustryId) : null,
    businessClassificationId: data.businessClassificationId ? Number(data.businessClassificationId) : null,
    recruiterId: Number(data.recruiterId),
    recruitmentSourceIds: cleanArray(data.recruitmentSourceIds),
    status: data.status,
    tagIds: cleanArray(data.tagIds),

    startDate: data.startDate ? dayjs(data.startDate).format(DATE_FORMAT_FOR_API) : null,
    endDate: data.endDate ? dayjs(data.endDate).format(DATE_FORMAT_FOR_API) : null,

    matchOfficeId: data.matchOfficeId ?? null,
    statusMemo: data.statusMemo ?? null,
    managementNumber: data.managementNumber ?? null,
    jobName: data.jobName,

    companySpecificBusinessClassificationIds: cleanArray(data.companySpecificBusinessClassificationIds),

    recruitmentNumber: data.recruitmentNumber ?? null,
    referralFee: data.referralFee ?? null,
    referralFeeCost: data.referralFeeCost ?? null,
    clientType: cleanArray(data.clientType),
    recruiterNote: data.recruiterNote ?? null,
    businessContent: data.businessContent ?? null,

    employmentType: data.employmentType ?? null,
    salaryType: data.salaryType ?? null,
    jobSalarySummaries:
      cleanArray(data.jobSalarySummaries)?.map(({ _clientId, ...rest }) => ({
        ...rest,
        totalAmount: parseNumberWithCommas(rest.totalAmount),
        deductionAmount: parseNumberWithCommas(rest.deductionAmount),
        netSalaryEstimate: parseNumberWithCommas(rest.netSalaryEstimate),
      })) ?? null,
    otherAllowanceDescription: data.otherAllowanceDescription ?? null,
    overtimeDescription: data.overtimeDescription ?? null,

    bonus: data.bonus ?? null,
    bonusInfo: data.bonusInfo ?? null,
    salaryRaise: data.salaryRaise ?? null,
    salaryRaiseTime: data.salaryRaiseTime ?? null,
    employmentConditionNote: data.employmentConditionNote ?? null,

    workplace: data.workplace ?? null,
    workplaceDetail: data.workplaceDetail ?? null,
    jobWorkingTimes: cleanArray(data.jobWorkingTimes)?.map((wt) => ({
      ...wt,
      workingTimeStart: normalizeTimeString(wt.workingTimeStart),
      workingTimeEnd: normalizeTimeString(wt.workingTimeEnd),
    })) ?? null,
    regularHolidays: data.regularHolidays ?? null,
    irregularHolidays: data.irregularHolidays ?? null,
    totalAnnualHolidays: data.totalAnnualHolidays ?? null,

    japaneseLevelType: data.japaneseLevelType ?? null,
    japaneseLevelOther: data.japaneseLevelOther ?? null,
    skillsExperience: data.skillsExperience ?? null,
    otherApplicationRequirements: data.otherApplicationRequirements ?? null,
    ageLimit: data.ageLimit ?? null,

    nationalityIds: cleanArray(data.nationalityIds),
    recruitmentLocationType: data.recruitmentLocationType ?? null,
    desiredHiringPeriod: data.desiredHiringPeriod ?? null,
    genders: data.gender ? [data.gender] : null,
    residenceStatusIds: cleanArray(data.residenceStatusIds),
    specificSkillCertificateIds: cleanArray(data.specificSkillCertificateIds),

    jobDocumentUrl: data.jobDocumentUrl ?? null,
    jobDocumentFiles: data.jobDocumentFiles ?? null,
    jobDocumentFilePaths: JSON.stringify(data.jobDocumentFilePaths) ?? null,
    selectionProcess: data.selectionProcess ?? null,
    companyAccommodation: data.companyAccommodation ?? null,
    specifiedSkillNo2: data.specifiedSkillNo2 ?? null,
    otherBenefits: data.otherBenefits ?? null,
    insuranceStatus: data.insuranceStatus ?? null,
    selfExpense: cleanArray(data.selfExpense),
    companyFeatures: data.companyFeatures ?? null,
    foreignEmployees: data.foreignEmployees ?? null,
    onboardingSupport: cleanArray(data.onboardingSupport),
    relatedFiles: data.relatedFiles ?? null,
    relatedFilePaths: JSON.stringify(data.relatedFilePaths) ?? null,
    relatedFileTitles: cleanArray(data.relatedFileTitles) ?? null,
  };

  const payload = raw as TCreateJobReqDto;
  return payload;
};

export const convertResToFormData = (data: TGetJobDto): Partial<TJobForm> | undefined => {
  if (!data) return undefined;

  const response = {
    companyId: data.companyId,
    companySpecificIndustryId: data.companySpecificIndustryId ? Number(data.companySpecificIndustryId) : null,
    businessClassificationId: data.businessClassificationId ? Number(data.businessClassificationId) : null,
    recruiterId: Number(data.recruiterId),
    recruitmentSourceIds: data.recruitmentSourceIds ? data.recruitmentSourceIds.map((item) => item.toString()) : null,
    status: data.status,
    tagIds: data.tagIds ?? null,

    startDate: data.startDate ? dayjs(data.startDate) : null,
    endDate: data.endDate ? dayjs(data.endDate) : null,
    matchOfficeId: data.matchOfficeId ?? undefined,
    statusMemo: data.statusMemo ?? null,
    managementNumber: data.managementNumber ?? null,
    jobName: data.jobName,
    companySpecificBusinessClassificationIds: data.companySpecificBusinessClassificationIds
      ? data.companySpecificBusinessClassificationIds.map((item) => item.toString())
      : null,

    recruitmentNumber: data.recruitmentNumber ?? null,
    referralFee: data.referralFee ?? null,
    referralFeeCost: data.referralFeeCost ?? null,
    clientType: data.clientType ?? null,
    recruiterNote: data.recruiterNote ?? null,
    businessContent: data.businessContent ?? null,
    employmentType: data.employmentType ?? null,
    salaryType: data.salaryType ?? null,
    jobSalarySummaries:
      data?.jobSalarySummaries && data?.jobSalarySummaries.length > 0
        ? data.jobSalarySummaries.map((summary) => ({
            id: summary.id,
            _clientId: uuidv4(),
            totalAmount: summary.totalAmount,
            totalAmountBreakdown: summary.totalAmountBreakdown,
            deductionAmount: summary.deductionAmount,
            deductionDetail: summary.deductionDetail,
            netSalaryEstimate: summary.netSalaryEstimate,
            netSalaryEstimateNote: summary.netSalaryEstimateNote,
          }))
        : [createJobSalarySummaryForm()],
    otherAllowanceDescription: data.otherAllowanceDescription ?? null,
    overtimeDescription: data.overtimeDescription ?? null,
    bonus: data.bonus ?? null,
    bonusInfo: data.bonusInfo ?? null,
    salaryRaise: data.salaryRaise ?? null,
    salaryRaiseTime: data.salaryRaiseTime ?? null,
    employmentConditionNote: data.employmentConditionNote ?? null,
    workplace: data.workplace ?? null,
    workplaceDetail: data.workplaceDetail ?? null,
    jobWorkingTimes:
      data?.jobWorkingTimes && data?.jobWorkingTimes.length > 0
        ? data.jobWorkingTimes.map((workingTime) => {
            let workingTimeBreakTime = null;
            if (workingTime.workingTimeBreakTime) {
              workingTimeBreakTime = Number(workingTime.workingTimeBreakTime);
            }
            return {
              id: workingTime.id,
              workingTimeStart: workingTime.workingTimeStart,
              workingTimeEnd: workingTime.workingTimeEnd,
              workingTimeBreakTime: workingTimeBreakTime,
            };
          })
        : [initJobWorkingTimeForm],
    regularHolidays: data.regularHolidays ?? null,
    irregularHolidays: data.irregularHolidays ?? null,
    totalAnnualHolidays: data.totalAnnualHolidays ?? null,
    japaneseLevelType: data.japaneseLevelType ?? null,
    japaneseLevelOther: data.japaneseLevelOther ?? null,
    skillsExperience: data.skillsExperience ?? null,
    otherApplicationRequirements: data.otherApplicationRequirements ?? null,
    ageLimit: data.ageLimit ?? null,
    nationalityIds: data.nationalityIds ? data.nationalityIds.map((item) => item.toString()) : null,
    recruitmentLocationType: data.recruitmentLocationType ?? null,
    desiredHiringPeriod: data.desiredHiringPeriod ?? null,
    gender: data.genders && data.genders.length > 0 ? data.genders[0].toString() : null,
    residenceStatusIds: data.residenceStatusIds ?? null,
    specificSkillCertificateIds: data.specificSkillCertificateIds ? data.specificSkillCertificateIds.map((item) => item.toString()) : null,

    jobDocumentUrl: data.jobDocumentUrl ?? null,
    jobDocumentFilePaths: data.jobDocumentFilePaths ?? null,
    selectionProcess: data.selectionProcess ?? null,
    companyAccommodation: data.companyAccommodation ?? null,
    specifiedSkillNo2: data.specifiedSkillNo2 ?? null,
    otherBenefits: data.otherBenefits ?? null,
    insuranceStatus: data.insuranceStatus ?? null,
    selfExpense: data.selfExpense ?? null,
    companyFeatures: data.companyFeatures ?? null,
    foreignEmployees: data.foreignEmployees ?? null,
    onboardingSupport: formatOnboardingSupportField(data.onboardingSupport) ?? null,
    company: data.company ?? null,
    businessClassification: data.businessClassification ?? null,
    companySpecificIndustry: data.companySpecificIndustry ?? null,
    recruiter: data.recruiter ?? null,
    jobUploadFiles: data.jobUploadFiles,
    nationalities: data.nationalities,
    cases: data.cases,
    matchOffice: data.matchOffice ?? null,
    companyExists: data.companyExists,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
    shareLinks: data.shareLinks ?? null,
  };

  return response;
};

export const initJobWorkingTimeForm = {
  workingTimeStart: null,
  workingTimeEnd: null,
  workingTimeBreakTime: null,
};

export const createJobSalarySummaryForm = () => ({
  _clientId: uuidv4(),
  totalAmount: null,
  totalAmountBreakdown: null,
  deductionAmount: null,
  deductionDetail: null,
  netSalaryEstimate: null,
  netSalaryEstimateNote: null,
});

/**
 * Maps OCR scan labels to enum values for Select components.
 * OCR may return Japanese labels or lowercase values that don't match expected enum values.
 */

// Employment type: OCR may return Japanese labels
const EMPLOYMENT_TYPE_LABEL_MAP: Record<string, string> = {
  "正社員(期間の定めなし)": "direct_employment",
  正社員: "direct_employment",
  派遣社員: "temporary_employment",
  派遣: "temporary_employment",
  direct_employment: "direct_employment",
  temporary_employment: "temporary_employment",
};

// Salary type: OCR may return Japanese labels
const SALARY_TYPE_LABEL_MAP: Record<string, string> = {
  月給: "monthly_salary",
  日給: "daily_salary",
  時給: "hourly_wage",
  monthly_salary: "monthly_salary",
  daily_salary: "daily_salary",
  hourly_wage: "hourly_wage",
};

// Japanese level: API enum values are lowercase (n1, n2, n3, n4)
const JAPANESE_LEVEL_LABEL_MAP: Record<string, string> = {
  n1: "n1",
  n2: "n2",
  n3: "n3",
  n4: "n4",
  n5: "n5",
  N1: "n1",
  N2: "n2",
  N3: "n3",
  N4: "n4",
  N5: "n5",
  // Japanese labels
  日本語能力試験N1: "n1",
  日本語能力試験N2: "n2",
  日本語能力試験N3: "n3",
  日本語能力試験N4: "n4",
  日本語能力試験N5: "n5",
};

/**
 * Maps an OCR scan value to its corresponding enum value.
 * Returns undefined if no mapping exists, allowing the Select to handle invalid values gracefully.
 */
const mapOcrValueToEnum = (value: unknown, labelMap: Record<string, string>): string | null => {
  if (value === null || value === undefined) return null;
  const stringValue = String(value).trim();
  return labelMap[stringValue] ?? null;
};

/**
 * Converts OCR scan data to form-compatible values.
 * This function maps Japanese labels and lowercase values to proper enum values.
 */
export const convertOcrScanDataToFormData = (scanData: Record<string, unknown>): Partial<TJobForm> => {
  const result: Partial<TJobForm> = { ...scanData } as Partial<TJobForm>;

  // Map employment type
  if (scanData.employmentType !== undefined) {
    result.employmentType = mapOcrValueToEnum(scanData.employmentType, EMPLOYMENT_TYPE_LABEL_MAP);
  }

  // Map salary type
  if (scanData.salaryType !== undefined) {
    result.salaryType = mapOcrValueToEnum(scanData.salaryType, SALARY_TYPE_LABEL_MAP);
  }

  // Map Japanese level type
  if (scanData.japaneseLevelType !== undefined) {
    result.japaneseLevelType = mapOcrValueToEnum(scanData.japaneseLevelType, JAPANESE_LEVEL_LABEL_MAP);
  }

  // Convert jobSalarySummaries numeric fields - OCR may return comma-formatted strings
  if (Array.isArray(scanData.jobSalarySummaries)) {
    result.jobSalarySummaries = (scanData.jobSalarySummaries as Record<string, unknown>[]).map((summary) => ({
      ...summary,
      _clientId: uuidv4(),
      totalAmount: parseNumberWithCommas(summary.totalAmount as string | number | null | undefined),
      deductionAmount: parseNumberWithCommas(summary.deductionAmount as string | number | null | undefined),
      netSalaryEstimate: parseNumberWithCommas(summary.netSalaryEstimate as string | number | null | undefined),
    })) as TJobForm["jobSalarySummaries"];
  }

  // Convert other numeric fields that may have comma formatting from OCR
  if (scanData.recruitmentNumber !== undefined) {
    result.recruitmentNumber = parseNumberWithCommas(scanData.recruitmentNumber as string | number | null | undefined);
  }
  if (scanData.referralFee !== undefined) {
    result.referralFee = parseNumberWithCommas(scanData.referralFee as string | number | null | undefined);
  }
  if (scanData.referralFeeCost !== undefined) {
    result.referralFeeCost = parseNumberWithCommas(scanData.referralFeeCost as string | number | null | undefined);
  }
  if (scanData.totalAnnualHolidays !== undefined) {
    result.totalAnnualHolidays = parseNumberWithCommas(scanData.totalAnnualHolidays as string | number | null | undefined);
  }

  // recruiterId: Exclude from OCR scan data - recruiters should be manually selected
  // OCR cannot determine which internal user should handle the job
  delete (result as Record<string, unknown>).recruiterId;

  return result;
};
